import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getIpeUiLocale } from '@/i18n/storage';
import { useNavigate } from 'react-router-dom';
import { Bell, Megaphone } from 'lucide-react';
import IpeRechargeCartButton from '@/components/ipe/IpeRechargeCartButton';
import { useIpeAuth } from '@/context/IpeAuthContext';
import { fundingAuthAPI, systemAnnouncementAPI } from '@/utils/api';
import { SYSTEM_NOTIFICATIONS } from '@/pages/ipe/workbench/data/systemNotifications';
import { buildPersonalNotifications } from '@/pages/ipe/workbench/utils/buildPersonalNotifications';
import { openStoragePurchaseModal } from '@/pages/ipe/workbench/utils/openStoragePurchaseModal';
import {
  openCreditsPurchaseModal,
  openMembershipPurchaseModal,
  openShopPurchaseModal,
} from '@/pages/ipe/workbench/utils/openPurchaseModal';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import SystemAnnouncementPopup from '@/pages/ipe/workbench/modals/SystemAnnouncementPopup';
import { locateSystemAnnouncementGuide } from '@/pages/ipe/workbench/utils/systemAnnouncementGuideNav';
import {
  dismissSystemAnnouncementPopup,
  isSystemAnnouncementPopupDismissed,
} from '@/pages/ipe/workbench/utils/systemAnnouncementPopupStore';
import {
  countUnreadNotifications,
  isNotificationRead,
  markNotificationRead,
} from '@/pages/ipe/workbench/utils/workbenchNotificationStore';

function formatNotificationTime(value) {
  if (!value) return '';
  const date = typeof value === 'number' ? new Date(value) : new Date(String(value));
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(getIpeUiLocale(), {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function NotificationBadge({ count }) {
  if (!count || count <= 0) return null;
  const label = count > 99 ? '99+' : String(count);
  return (
    <span className="ipe-workbench-notify__badge" aria-hidden="true">
      {label}
    </span>
  );
}

function NotificationPanel({
  title,
  emptyText,
  items,
  channel,
  onAction,
  readRevision,
}) {
  return (
    <div className="ipe-workbench-notify__panel" role="dialog" aria-label={title}>
      <div className="ipe-workbench-notify__panel-head">
        <h3 className="ipe-workbench-notify__panel-title">{title}</h3>
      </div>
      <div className="ipe-workbench-notify__panel-body">
        {items.length === 0 ? (
          <p className="ipe-workbench-notify__empty">{emptyText}</p>
        ) : (
          <ul className="ipe-workbench-notify__list">
            {items.map((item) => {
              const isRead = isNotificationRead(channel, item.id);
              const styleClass = channel === 'system' && item.style
                ? ` ipe-workbench-notify__item--${item.style}`
                : '';
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`ipe-workbench-notify__item${isRead ? ' is-read' : ''}${styleClass}`}
                    onClick={() => {
                      if (!isNotificationRead(channel, item.id)) {
                        markNotificationRead(channel, item.id);
                      }
                      onAction?.(item);
                    }}
                  >
                    <div className="ipe-workbench-notify__item-head">
                      <span className="ipe-workbench-notify__item-title">{item.title}</span>
                      {!isRead ? <span className="ipe-workbench-notify__dot" aria-hidden="true" /> : null}
                    </div>
                    <p className="ipe-workbench-notify__item-message">{item.message}</p>
                    <div className="ipe-workbench-notify__item-foot">
                      <time className="ipe-workbench-notify__item-time" dateTime={String(item.createdAt || '')}>
                        {formatNotificationTime(item.createdAt)}
                      </time>
                      {item.actionLabel ? (
                        <span className="ipe-workbench-notify__item-action">{item.actionLabel}</span>
                      ) : null}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <span className="ipe-workbench-notify__read-revision" aria-hidden="true">
        {readRevision}
      </span>
    </div>
  );
}

function NotificationTrigger({
  label,
  icon: Icon,
  unreadCount,
  isOpen,
  onToggle,
  panel,
  buttonRef,
}) {
  return (
    <div className={`ipe-workbench-notify__trigger${isOpen ? ' is-open' : ''}`}>
      <button
        ref={buttonRef}
        type="button"
        className="ipe-workbench-notify__btn"
        aria-label={label}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={onToggle}
      >
        <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
        <NotificationBadge count={unreadCount} />
      </button>
      {isOpen ? panel : null}
    </div>
  );
}

export default function WorkbenchNotificationCenter() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useIpeAuth();
  const userId = user?.id ?? user?.userId ?? null;
  const workspaceMode = useIpeWorkspaceMode();
  const workspaceNotifyLabel = workspaceMode === 'team' ? t('teamNotifications') : t('personalNotifications');
  const rootRef = useRef(null);
  const personalBtnRef = useRef(null);
  const systemBtnRef = useRef(null);
  const [openChannel, setOpenChannel] = useState(null);
  const [accountCenter, setAccountCenter] = useState(null);
  const [systemItems, setSystemItems] = useState(SYSTEM_NOTIFICATIONS);
  const [popupAnnouncement, setPopupAnnouncement] = useState(null);
  const [popupRevision, setPopupRevision] = useState(0);
  const [readRevision, setReadRevision] = useState(0);

  const personalNotifications = useMemo(
    () => {
      void i18n.language;
      return buildPersonalNotifications(accountCenter, workspaceMode);
    },
    [accountCenter, workspaceMode, i18n.language],
  );

  const systemNotifications = useMemo(
    () => [...systemItems].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    ),
    [systemItems],
  );

  // readRevision 变化时重新从 localStorage 统计未读数
  void readRevision;
  const personalUnread = countUnreadNotifications('personal', personalNotifications);
  const systemUnread = countUnreadNotifications('system', systemNotifications);

  const bumpReadState = useCallback(() => {
    setReadRevision((v) => v + 1);
  }, []);

  const loadAccountCenter = useCallback(async () => {
    try {
      const data = await fundingAuthAPI.getAccountCenter();
      setAccountCenter(data);
    } catch {
      setAccountCenter(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fundingAuthAPI.getAccountCenter();
        if (!cancelled) setAccountCenter(data);
      } catch {
        if (!cancelled) setAccountCenter(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceMode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await systemAnnouncementAPI.list();
        if (!cancelled) {
          setSystemItems(Array.isArray(res?.items) ? res.items : []);
        }
      } catch {
        if (!cancelled) setSystemItems(SYSTEM_NOTIFICATIONS);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceMode]);

  useEffect(() => {
    void popupRevision;
    const next = systemItems.find((item) => (
      item?.id
      && item.popupEligible !== false
      && !isSystemAnnouncementPopupDismissed(userId, item.id)
    ));
    setPopupAnnouncement(next || null);
  }, [systemItems, userId, popupRevision]);

  useEffect(() => {
    const onRefresh = () => {
      loadAccountCenter();
    };
    // mode-changed 已由 workspaceMode effect 覆盖；避免切换时双拉 account
    window.addEventListener('ipe:workspace-refresh', onRefresh);
    window.addEventListener('ipe:storage-usage-changed', onRefresh);
    window.addEventListener('ipe:credits-consumed', onRefresh);
    return () => {
      window.removeEventListener('ipe:workspace-refresh', onRefresh);
      window.removeEventListener('ipe:storage-usage-changed', onRefresh);
      window.removeEventListener('ipe:credits-consumed', onRefresh);
    };
  }, [loadAccountCenter]);

  useEffect(() => {
    if (!openChannel) return undefined;
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpenChannel(null);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenChannel(null);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openChannel]);

  const handlePersonalAction = useCallback((item) => {
    bumpReadState();
    setOpenChannel(null);
    switch (item.actionType) {
      case 'storage-purchase':
        openStoragePurchaseModal(workspaceMode);
        break;
      case 'credits-purchase':
        openCreditsPurchaseModal(workspaceMode);
        break;
      case 'shop-purchase':
        openShopPurchaseModal(workspaceMode);
        break;
      case 'membership-purchase':
        openMembershipPurchaseModal(workspaceMode);
        break;
      case 'account-center':
        window.dispatchEvent(new CustomEvent('ipe:open-account-center'));
        break;
      default:
        break;
    }
  }, [bumpReadState, workspaceMode]);

  const handleSystemAction = useCallback((item) => {
    bumpReadState();
    setOpenChannel(null);
    if (item.actionUrl) {
      if (item.actionUrl.startsWith('http')) {
        window.open(item.actionUrl, '_blank', 'noopener,noreferrer');
      } else {
        window.location.assign(item.actionUrl);
      }
    }
  }, [bumpReadState]);

  const dismissPopup = useCallback((item) => {
    if (!item?.id) {
      setPopupAnnouncement(null);
      return;
    }
    dismissSystemAnnouncementPopup(userId, item.id);
    if (!isNotificationRead('system', item.id)) {
      markNotificationRead('system', item.id);
      bumpReadState();
    }
    setPopupRevision((v) => v + 1);
  }, [bumpReadState, userId]);

  const handlePopupAction = useCallback((item) => {
    dismissPopup(item);
    if (item?.actionUrl) {
      if (item.actionUrl.startsWith('http')) {
        window.open(item.actionUrl, '_blank', 'noopener,noreferrer');
      } else {
        locateSystemAnnouncementGuide(navigate, item.actionUrl);
      }
    }
  }, [dismissPopup, navigate]);

  const handleGuideLocate = useCallback((step) => {
    locateSystemAnnouncementGuide(navigate, step?.actionUrl);
  }, [navigate]);

  const toggleChannel = useCallback((channel) => {
    setOpenChannel((current) => (current === channel ? null : channel));
  }, []);

  return (
    <div className="ipe-workbench-notify-cluster">
      <SystemAnnouncementPopup
        open={Boolean(popupAnnouncement)}
        announcement={popupAnnouncement}
        mode="tour"
        onDismiss={() => dismissPopup(popupAnnouncement)}
        onAction={handlePopupAction}
        onGuideLocate={handleGuideLocate}
      />
      <div ref={rootRef} className="ipe-workbench-notify">
        <NotificationTrigger
        label={workspaceNotifyLabel}
        icon={Bell}
        unreadCount={personalUnread}
        isOpen={openChannel === 'personal'}
        buttonRef={personalBtnRef}
        onToggle={() => toggleChannel('personal')}
        panel={(
          <NotificationPanel
            title={workspaceNotifyLabel}
            emptyText={t('emptyNotifications', { label: workspaceNotifyLabel })}
            items={personalNotifications}
            channel="personal"
            readRevision={readRevision}
            onAction={handlePersonalAction}
          />
        )}
      />
      <NotificationTrigger
        label={t('systemNotifications')}
        icon={Megaphone}
        unreadCount={systemUnread}
        isOpen={openChannel === 'system'}
        buttonRef={systemBtnRef}
        onToggle={() => toggleChannel('system')}
        panel={(
          <NotificationPanel
            title={t('systemNotifications')}
            emptyText={t('emptySystemNotifications')}
            items={systemNotifications}
            channel="system"
            readRevision={readRevision}
            onAction={handleSystemAction}
          />
        )}
      />
      </div>
      <IpeRechargeCartButton />
    </div>
  );
}
